import { useEffect, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { admin, palette, radii, typography } from '../../../styles/tokens';
import { useFieldErrors, type FieldErrors } from '../hooks/useFieldErrors';

interface TaxItemProps {
  businessId: string;
  onBack: () => void;
  onSaved: () => void;
}

type TaxMode = 'charge' | 'exempt' | null;

const PROVINCE_DEFAULT_RATE: Record<string, number> = {
  ON: 13, NB: 15, NS: 15, PE: 15, NL: 15,
  BC: 5, AB: 5, SK: 5, MB: 5, QC: 5, YT: 5, NT: 5, NU: 5,
};

function defaultRateFor(province: string | null | undefined): number {
  if (!province) return 13;
  return PROVINCE_DEFAULT_RATE[province] ?? 13;
}

export default function TaxItem({ businessId, onBack, onSaved }: TaxItemProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [taxMode, setTaxMode] = useState<TaxMode>(null);
  const [hstNumber, setHstNumber] = useState('');
  const [hstRatePct, setHstRatePct] = useState('13');
  const [province, setProvince] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    (async () => {
      const [{ data: biz, error: bizErr }, { data: bfs }] = await Promise.all([
        supabase
          .from('businesses')
          .select('tax_mode, province')
          .eq('id', businessId)
          .maybeSingle(),
        supabase
          .from('business_financial_settings')
          .select('hst_rate, hst_number')
          .eq('business_id', businessId)
          .maybeSingle(),
      ]);
      if (cancelled) return;
      if (bizErr) {
        setError(`Couldn't load tax settings: ${bizErr.message}`);
        setLoading(false);
        return;
      }
      const prov = biz?.province ?? null;
      setProvince(prov);
      setTaxMode((biz?.tax_mode as TaxMode) ?? null);
      setHstNumber(bfs?.hst_number ?? '');
      const rateDecimal = bfs?.hst_rate;
      if (typeof rateDecimal === 'number' && rateDecimal > 0) {
        setHstRatePct(String(Math.round(rateDecimal * 1000) / 10));
      } else {
        setHstRatePct(String(defaultRateFor(prov)));
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [businessId]);

  const rateNum = parseFloat(hstRatePct);

  type TaxField = 'rate';
  const validate = (): FieldErrors<TaxField> => {
    const e: FieldErrors<TaxField> = {};
    if (taxMode === 'charge' && hstRatePct.trim()) {
      if (!(rateNum > 0 && rateNum < 100)) {
        e.rate = 'Enter a valid tax rate.';
      }
    }
    return e;
  };
  const { canSave: formatOk, showError, handleBlur, markAllTouched } =
    useFieldErrors<TaxField>(validate);

  // tax_mode is a fundamental mode-selection, not a typical field — until
  // it's picked there is literally nothing to save. Charge branch also needs
  // a rate to write to BFS.
  const hasSomethingToSave =
    taxMode === 'exempt' || (taxMode === 'charge' && hstRatePct.trim() !== '');
  const canSave = !saving && formatOk && hasSomethingToSave;

  async function handleSave() {
    if (!formatOk) {
      markAllTouched();
      return;
    }
    setError(null);
    setSaving(true);
    try {
      // §Settings.HstNumberDuplication D5 (Option B): canonical RPC routes tax_mode +
      // hst_number through update_business_money_settings — gives HST regex validation
      // + activity_log writer ('tax_mode_changed') from S137 substrate. The bfs.hst_rate
      // upsert below stays as a parallel write because the RPC body doesn't accept
      // p_hst_rate yet (§WorldClass.4 Tier 2 substrate will subsume by extending the
      // RPC; until then the two-call pattern is acceptable v1 per Cowork Step 0 ack).
      if (taxMode === 'exempt') {
        const { data: result, error: e1 } = await supabase.rpc('update_business_money_settings', {
          p_business_id: businessId,
          p_tax_mode: 'exempt',
        });
        if (e1) throw e1;
        if (result && !result.success) {
          throw new Error(result.error?.code ?? 'UPDATE_FAILED');
        }
      } else if (taxMode === 'charge') {
        const { data: result, error: e1 } = await supabase.rpc('update_business_money_settings', {
          p_business_id: businessId,
          p_tax_mode: 'charge',
          p_hst_number: hstNumber.trim() || null,
        });
        if (e1) throw e1;
        if (result && !result.success) {
          throw new Error(result.error?.code ?? 'UPDATE_FAILED');
        }

        // Parallel hst_rate write (canonical RPC signature unchanged — no p_hst_rate yet).
        const { error: e2 } = await supabase
          .from('business_financial_settings')
          .upsert(
            {
              business_id: businessId,
              hst_rate: rateNum / 100,
            },
            { onConflict: 'business_id' },
          );
        if (e2) throw e2;
      }
      setSaving(false);
      onSaved();
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setError(`Couldn't save tax settings: ${message}`);
      setSaving(false);
    }
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '8px 4px 0' }}>
      <button
        type="button"
        onClick={onBack}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '6px 8px 6px 4px',
          marginBottom: 16,
          background: 'transparent',
          border: 'none',
          color: admin.caption,
          fontSize: typography.size.caption,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        <ChevronLeft size={16} />
        <span>Setup guide</span>
        <span style={{ color: palette.g400, margin: '0 4px' }}>›</span>
        <span style={{ color: admin.body }}>Set up tax</span>
      </button>

      <h1
        style={{
          margin: 0,
          fontSize: typography.size.title,
          fontWeight: typography.weight.bold,
          color: admin.header,
          letterSpacing: '-0.01em',
        }}
      >
        Set up tax
      </h1>
      <p
        style={{
          margin: '8px 0 24px',
          fontSize: typography.size.body,
          color: admin.body,
          lineHeight: typography.lineHeight.relaxed,
          maxWidth: 560,
        }}
      >
        Do you charge tax on your services? If yes, we'll add it to every invoice automatically.
      </p>

      {loading ? (
        <div style={{ fontSize: typography.size.caption, color: admin.caption }}>Loading…</div>
      ) : (
        <>
          <fieldset
            style={{
              display: 'flex', flexDirection: 'column', gap: 12,
              marginBottom: 24, padding: 0, border: 'none', minInlineSize: 0,
            }}
          >
            <legend style={{
              padding: 0, marginBottom: 12,
              fontSize: typography.size.caption,
              fontWeight: typography.weight.semibold,
              color: admin.header,
            }}>
              Tax mode
            </legend>
            <RadioCard
              checked={taxMode === 'charge'}
              onSelect={() => setTaxMode('charge')}
              title="I charge tax on my services"
              subtitle="We'll add tax to every invoice and quote you send."
            />
            <RadioCard
              checked={taxMode === 'exempt'}
              onSelect={() => setTaxMode('exempt')}
              title="I don't charge tax"
              subtitle="Under the $30,000 threshold, or otherwise exempt."
            />
          </fieldset>

          {taxMode === 'charge' && (
            <div
              style={{
                background: palette.white,
                border: `1px solid ${palette.g150}`,
                borderRadius: radii.md,
                padding: 20,
                marginBottom: 24,
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
              }}
            >
              <Field
                label="Tax rate"
                hint={province ? `Default for ${province} is ${defaultRateFor(province)}%.` : undefined}
                error={showError('rate')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={hstRatePct}
                    onChange={e => setHstRatePct(e.target.value)}
                    onBlur={() => handleBlur('rate')}
                    style={{
                      ...inputStyle,
                      border: showError('rate') ? `1px solid ${palette.red}` : inputStyle.border,
                    }}
                  />
                  <span style={{ fontSize: typography.size.body, color: admin.caption }}>%</span>
                </div>
              </Field>

              <Field label="HST / GST number (optional)" hint="Leave blank if you're not registered yet.">
                <input
                  type="text"
                  value={hstNumber}
                  onChange={e => setHstNumber(e.target.value)}
                  placeholder="123456789RT0001"
                  style={{ ...inputStyle, width: 240 }}
                />
              </Field>
            </div>
          )}

          {taxMode === 'exempt' && (
            <div
              style={{
                background: palette.accentWash,
                border: `1px solid ${palette.accentSoft}`,
                borderRadius: radii.md,
                padding: 16,
                marginBottom: 24,
                fontSize: typography.size.caption,
                color: admin.body,
                lineHeight: typography.lineHeight.relaxed,
              }}
            >
              CrewBit won't add tax to any invoice or quote. If you cross the $30,000 threshold,
              you'll need to register with the CRA and turn tax on. We'll remind you.
            </div>
          )}

          {error && (
            <div
              style={{
                background: palette.redWash,
                border: `1px solid ${palette.red}`,
                borderRadius: radii.md,
                padding: 12,
                marginBottom: 16,
                color: palette.red,
                fontSize: typography.size.caption,
              }}
            >
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              style={{
                padding: '10px 20px',
                borderRadius: radii.md,
                border: 'none',
                background: canSave ? palette.accent : palette.g200,
                color: palette.white,
                fontSize: typography.size.body,
                fontWeight: typography.weight.semibold,
                cursor: canSave ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit',
              }}
            >
              {saving ? 'Saving…' : 'Save tax settings'}
            </button>
            <button
              type="button"
              onClick={onBack}
              disabled={saving}
              style={{
                padding: '10px 16px',
                borderRadius: radii.md,
                border: 'none',
                background: 'transparent',
                color: admin.caption,
                fontSize: typography.size.body,
                cursor: saving ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '8px 10px',
  borderRadius: radii.sm,
  border: `1px solid ${palette.g200}`,
  fontSize: typography.size.body,
  fontFamily: 'inherit',
  outline: 'none',
  width: 100,
};

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: typography.size.caption,
          fontWeight: typography.weight.semibold,
          color: admin.header,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      {children}
      {hint && !error && (
        <div
          style={{
            fontSize: typography.size.micro,
            color: admin.caption,
            marginTop: 4,
          }}
        >
          {hint}
        </div>
      )}
      {error && (
        <div
          style={{
            fontSize: typography.size.micro,
            color: palette.red,
            marginTop: 4,
            fontWeight: typography.weight.medium,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

function RadioCard({
  checked,
  onSelect,
  title,
  subtitle,
}: {
  checked: boolean;
  onSelect: () => void;
  title: string;
  subtitle: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      onClick={onSelect}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: 16,
        borderRadius: radii.md,
        border: `1.5px solid ${checked ? palette.accent : palette.g200}`,
        background: checked ? palette.accentWash : palette.white,
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'inherit',
        transition: 'all 150ms ease',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          marginTop: 2,
          width: 18,
          height: 18,
          borderRadius: '50%',
          border: `2px solid ${checked ? palette.accent : palette.g300}`,
          background: palette.white,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {checked && (
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: palette.accent,
            }}
          />
        )}
      </div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: typography.size.body,
            fontWeight: typography.weight.semibold,
            color: admin.header,
            marginBottom: 2,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: typography.size.caption,
            color: admin.caption,
            lineHeight: typography.lineHeight.normal,
          }}
        >
          {subtitle}
        </div>
      </div>
    </button>
  );
}
